@if (entity(); as currentEntity) {
  <div class="page-header">
    <h1>{{ entityTitle() }}</h1>
  </div>

  <mat-card>
    <mat-card-content>
      <div class="table-toolbar">
        <div class="chips-area">
          @for (chip of filterChips(); track $index) {
            <mat-chip>{{ chip }}</mat-chip>
          }
        </div>
        <div class="actions-area">
          <button mat-stroked-button (click)="openFilter()">
            <mat-icon>filter_list</mat-icon>
            {{ 'common.filter' | t }}
          </button>
          @if (shouldShowStatusQuickFilters()) {
            @for (status of quickStatusOptions(); track $index) {
              <button
                mat-stroked-button
                [class.quick-filter-active]="isQuickStatusActive(status)"
                (click)="setQuickStatusFilter(status)"
              >
                {{ ('status.' + status) | t }}
              </button>
            }
            <button mat-button [disabled]="!filter().status" (click)="setQuickStatusFilter('')">
              {{ 'common.all' | t }}
            </button>
          }
          <button mat-button (click)="clearFilters()">{{ 'resourceList.clearFilters' | t }}</button>
          @if (canAddCurrentResource()) {
            <button mat-flat-button color="primary" (click)="openAddResource()" [disabled]="listLoading()">
              <mat-icon>add</mat-icon>
              {{ 'common.add' | t }} {{ entityAddLabel() }}
            </button>
          }
        </div>
      </div>

      <div class="table-wrapper">
        @if (listLoading()) {
          <mat-progress-bar mode="indeterminate" />
        }
        <table mat-table [dataSource]="pagedRows()" class="mat-elevation-z0">
          @for (column of currentEntity.columns; track column.key) {
            <ng-container [matColumnDef]="column.key">
              <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel(column.key, column.label) }}</th>
              <td mat-cell *matCellDef="let row">
                @if (column.key === 'fullName') {
                  <a
                    class="cell-link person-name-cell"
                    href=""
                    [matTooltip]="getLinkedExternalReference(row, column.key)"
                    [matTooltipDisabled]="!getLinkedExternalReference(row, column.key)"
                    (click)="openLinkedDetails(row, column.key, $event)"
                  >
                    <span>{{ row['name'] }}</span>
                    <span>{{ row['surname'] }}</span>
                  </a>
                } @else if (column.key === 'monthlyLessonHours') {
                  @if (getMonthlyInstructorLessonsView(row); as lessonHours) {
                    <button
                      type="button"
                      class="monthly-lessons-pill"
                      [attr.aria-label]="getMonthlyInstructorLessonsTooltip(row)"
                      [matTooltip]="getMonthlyInstructorLessonsTooltip(row)"
                      matTooltipClass="monthly-lessons-tooltip"
                      matTooltipPosition="below"
                      [matTooltipShowDelay]="120"
                      (click)="$event.stopPropagation()"
                    >
                      <mat-icon>schedule</mat-icon>
                      <span>{{ lessonHours.totalHours }} {{ 'common.hoursShort' | t }}</span>
                    </button>
                  } @else {
                    -
                  }
                } @else if (column.key === 'courseProgress') {
                  @if (getCourseProgressView(row); as progress) {
                    <div class="course-progress-cell">
                      <div class="course-progress-top">
                        <span>{{ progress.name }}</span>
                        <strong>{{ progress.percent }}%</strong>
                      </div>
                      <div class="course-progress-meta">
                        {{ 'common.lessons' | t }}: {{ progress.completedHours }} / {{ progress.requiredHours }} {{ 'common.hoursShort' | t }}
                      </div>
                      <div class="course-progress-track">
                        <div class="course-progress-bar" [style.width.%]="progress.percent"></div>
                      </div>
                    </div>
                  } @else {
                    -
                  }
                } @else if (column.key === 'paymentStatus') {
                  <span class="payment-status-wrap">
                    <button
                      type="button"
                      [class]="getPaymentStatusClass(row)"
                      [attr.aria-label]="getPaymentBalanceAriaLabel(row)"
                      [matTooltip]="getPaymentBalanceTooltip(row)"
                      matTooltipClass="payment-balance-tooltip"
                      matTooltipPosition="below"
                      [matTooltipShowDelay]="120"
                      (click)="$event.stopPropagation()"
                    >
                      <mat-icon>{{ getPaymentStatusIcon(row) }}</mat-icon>
                      <span>{{ getPaymentBalanceValueLabel(row) }}</span>
                    </button>
                  </span>
                } @else if (column.key === 'status' && isInstructorList()) {
                  <span class="status-with-account">
                    <span>{{ getValue(row, column.key) }}</span>
                    <small>{{ getInstructorAccountStatusLabel(row) }}</small>
                  </span>
                } @else if (canLinkToDetails(row, column.key)) {
                  <a
                    class="cell-link"
                    href=""
                    [matTooltip]="getLinkedExternalReference(row, column.key)"
                    [matTooltipDisabled]="!getLinkedExternalReference(row, column.key)"
                    (click)="openLinkedDetails(row, column.key, $event)"
                  >{{ getValue(row, column.key) }}</a>
                } @else {
                  {{ getValue(row, column.key) }}
                }
              </td>
            </ng-container>
          }
          @if (hasActionsColumn(currentEntity.key)) {
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>{{ getColumnLabel('actions', 'common.actions') }}</th>
              <td mat-cell *matCellDef="let row">
                <div class="row-actions">
                  @if (canShowEditAction(row)) {
                    <button
                      mat-icon-button
                      class="row-action-button"
                      [matTooltip]="'common.edit' | t"
                      [attr.aria-label]="'common.edit' | t"
                      (click)="openEditEntity(row, $event)"
                    >
                      <mat-icon>edit</mat-icon>
                    </button>
                  }
                  @for (action of getQuickActions(row); track action.key) {
                    <button
                      mat-icon-button
                      class="row-action-button"
                      [matTooltip]="action.labelKey | t"
                      [attr.aria-label]="action.labelKey | t"
                      (click)="openQuickAction(row, action, $event)"
                    >
                      <mat-icon>{{ action.icon }}</mat-icon>
                    </button>
                  }
                  @for (action of getStatusActions(row); track action.nextStatus) {
                    <button
                      mat-icon-button
                      class="row-action-button"
                      [matTooltip]="action.labelKey | t"
                      [attr.aria-label]="action.labelKey | t"
                      (click)="applyStatusAction(row, action, $event)"
                      [disabled]="paymentSettlementsPending().has('' + row.id)"
                    >
                      <mat-icon>{{ action.icon }}</mat-icon>
                    </button>
                  }
                  @if (!canShowEditAction(row) && getQuickActions(row).length === 0 && getStatusActions(row).length === 0) {
                    <span class="no-actions">-</span>
                  }
                </div>
              </td>
            </ng-container>
          }

          <tr mat-header-row *matHeaderRowDef="displayedColumns()"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns()"
            class="clickable-row"
            (click)="openDetails(row.id)"
          ></tr>
        </table>
      </div>

      <mat-paginator
        [length]="totalRows()"
        [pageSize]="pageSize()"
        [pageIndex]="pageIndex()"
        [pageSizeOptions]="[5, 10, 20, 50]"
        (page)="onPageChange($event)"
      />
    </mat-card-content>
  </mat-card>
} @else {
  <mat-card>
    <mat-card-content>{{ 'resourceList.unknownType' | t }}</mat-card-content>
  </mat-card>
}
